// Real crystal structure per material, independent of the LAMMPS potential.
//
// A material's geometry (its lattice) is separate from the potential used to
// simulate it. Recipes already carry a structure for the materials they cover
// (metals, Si, GaN…), but the UFF-fallback path would otherwise place atoms on
// a generic cubic grid. This resolver gives *every crystalline material* a real
// lattice so its atomic structure is preserved even on the fallback:
//
//   • pure elements → their natural structure (fcc/bcc/hcp/diamond) at the
//     literature lattice constant, or, for elements without a tabulated one, an
//     fcc lattice whose constant is derived from the material's mass density
//     (so the packing/density is still physically right);
//   • simple ionic 1:1 binaries (NaCl-type) → rock-salt;
//   • everything else (mixtures, molecular liquids, amorphous solids) → null,
//     i.e. the stoichiometric cubic packing, which is the honest representation
//     when there is no single crystal structure.

import type { CrystalStructure } from "./crystal";
import { latticeSpacingAngstrom } from "./lammps";

export interface MaterialCrystal {
  structure: CrystalStructure;
  /** Lattice constant a (Å). */
  a: number;
  /** Optional c (Å) for hexagonal structures. */
  c?: number;
}

// Natural crystal structure + lattice constant (Å) of pure elements.
// Standard literature room-temperature values.
const ELEMENT_CRYSTAL: Record<string, MaterialCrystal> = {
  // fcc metals
  Al: { structure: "fcc", a: 4.05 },
  Cu: { structure: "fcc", a: 3.615 },
  Ag: { structure: "fcc", a: 4.085 },
  Au: { structure: "fcc", a: 4.078 },
  Ni: { structure: "fcc", a: 3.524 },
  Pt: { structure: "fcc", a: 3.924 },
  Pd: { structure: "fcc", a: 3.891 },
  Pb: { structure: "fcc", a: 4.951 },
  Ca: { structure: "fcc", a: 5.588 },
  Sr: { structure: "fcc", a: 6.085 },
  Rh: { structure: "fcc", a: 3.803 },
  Ir: { structure: "fcc", a: 3.839 },
  // bcc metals
  Fe: { structure: "bcc", a: 2.866 },
  W: { structure: "bcc", a: 3.165 },
  Cr: { structure: "bcc", a: 2.885 },
  Mo: { structure: "bcc", a: 3.147 },
  Na: { structure: "bcc", a: 4.291 },
  K: { structure: "bcc", a: 5.328 },
  V: { structure: "bcc", a: 3.024 },
  Nb: { structure: "bcc", a: 3.3 },
  Ta: { structure: "bcc", a: 3.306 },
  Ba: { structure: "bcc", a: 5.028 },
  // hcp metals
  Mg: { structure: "hcp", a: 3.209, c: 5.211 },
  Zn: { structure: "hcp", a: 2.665, c: 4.947 },
  Ti: { structure: "hcp", a: 2.951, c: 4.684 },
  Co: { structure: "hcp", a: 2.507, c: 4.069 },
  Zr: { structure: "hcp", a: 3.232, c: 5.147 },
  Cd: { structure: "hcp", a: 2.979, c: 5.62 },
  Be: { structure: "hcp", a: 2.286, c: 3.585 },
  Ru: { structure: "hcp", a: 2.706, c: 4.282 },
  // diamond cubic
  C: { structure: "diamond", a: 3.567 },
  Si: { structure: "diamond", a: 5.431 },
  Ge: { structure: "diamond", a: 5.658 },
  // noble gases (fcc as cryogenic solids)
  Ne: { structure: "fcc", a: 4.46 },
  Ar: { structure: "fcc", a: 5.26 },
  Kr: { structure: "fcc", a: 5.72 },
  Xe: { structure: "fcc", a: 6.2 },
};

// 1:1 ionic binaries that crystallise rock-salt, by element pair (sorted).
const ROCKSALT: Record<string, number> = {
  "Cl,Na": 5.64, // NaCl
  "Cl,K": 6.29, // KCl
  "Mg,O": 4.212, // MgO
  "Ca,O": 4.811, // CaO
  "Li,F": 4.026, // LiF
  "Cl,Li": 5.14, // LiCl
};

export interface CrystalComposition {
  id: string;
  n: number;
  /** Atomic weight (amu) for the density-derived fallback. */
  atomicWeight: number;
}

/**
 * Resolve a real crystal structure for a material from its composition and mass
 * density. Returns null when there is no single crystal structure (mixtures,
 * molecular/amorphous materials) — the caller then uses cubic stoichiometric
 * packing.
 */
export function crystalForMaterial(
  comp: CrystalComposition[],
  densityKgM3: number | undefined,
): MaterialCrystal | null {
  if (comp.length === 0) return null;

  // Pure element: known structure, else an fcc lattice sized from density.
  if (comp.length === 1) {
    const known = ELEMENT_CRYSTAL[comp[0].id];
    if (known) return known;
    // fcc: nearest-neighbour distance = a/√2, and the density-derived spacing
    // is that nn distance, so a = spacing·√2 → an fcc lattice at the right
    // density for any element.
    const spacing = latticeSpacingAngstrom(densityKgM3, comp[0].atomicWeight);
    return { structure: "fcc", a: spacing * Math.SQRT2 };
  }

  // 1:1 binary: rock-salt if it's a known ionic pair.
  if (comp.length === 2 && comp[0].n === comp[1].n) {
    const key = [comp[0].id, comp[1].id].sort().join(",");
    const a = ROCKSALT[key];
    if (a) return { structure: "rocksalt", a };
  }

  return null;
}
